import { Component, EventEmitter, inject, Input, OnChanges, Output, Signal, SimpleChanges } from '@angular/core';
import { OrderPublic } from '../../../../globalTypes/order.types';
import { ButtonComponent, ButtonDisplay } from "../../component/layout/element/button.component";
import { MatIcon } from "@angular/material/icon";
import { NameTagComponent } from "../../component/layout/element/name.tag.component";
import { LocationPublic } from '../../../../globalTypes/account.types';
import { Store } from '@ngrx/store';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { accountFeature } from '../../store/account.store';
import { lastValueFrom, take, takeUntil } from 'rxjs';
import { CurrencyPipe, DatePipe, JsonPipe } from '@angular/common';
import { dateTimeFormat, Status } from '../../../../globalTypes/base.types';
import { sendMessage, SendMessageVariables } from '@dataconnect/account';
import { snackBarError } from '../../helper/error.handling';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InteractionView } from '../../../../globalTypes/interactions.types';
import { InteractionEntryComponent } from '../../component/layout/element/interaction-entry.component';
import { Fulfillment, FulfillmentStatusType } from '../../../../globalTypes/fulfillment.types';
import { FormsModule } from '@angular/forms';
import { MatButton } from "@angular/material/button";
import { OrderService } from '../../services/order.service';
import { interactionsFeature, InteractionsPageActions } from '../../store/interactions.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { RefundDetails } from './refund-details';
import { RefundPublic } from '../../../../globalTypes/refund.types';
import { ReviewTileComponent } from "../../component/review/review.tile";
import { ReviewForEdit } from '../../../../globalTypes/review.types';
import { Router } from '@angular/router';
import { LOCATION_LABEL } from '../../../../globalTypes/configs';

@Component({
  selector: 'msh-purchase-order-details',
  imports: [ButtonComponent, RefundDetails, FormsModule, DatePipe, InteractionEntryComponent, CurrencyPipe, MatIcon, NameTagComponent, MatButton, ReviewTileComponent],
  template: `
  @if(!order){
    <div class="null my-5">Searching for order details</div>
  } @else {
    <div class="flex gap-3 items-center pb-2 mb-8">
      <div class="text-lg uppercase font-bold">
        Order #{{order.orderNumber}}
      </div>
      <div class="font-thin"> {{order.createdAt | date:dateTimeFormat}}</div>
    </div>
    @if(order.fulfillments_on_order; as items){
      @for(fulfillment of order.fulfillments_on_order; track fulfillment.id){
        @if(fulfillment.statusId !== 'archived'){
          <div class=" mt-4 mb-8">
            <msh-name-tag
              [label]="locations[fulfillment.fulfilledByLocationId].name"
              [url]="locations[fulfillment.fulfilledByLocationId].profileImage?.url"
              size="small"
            ></msh-name-tag>
            <div class=" mt-4 flex justify-between gap-4">
              <div class=" flex flex-col gap-4">
                @if(order.orderItems_by_fulfillment && order.orderItems_by_fulfillment[fulfillment.id]; as items){
                  @for(item of items; track item.id){
                    <div class=" flex gap-4">
                      <div class=" w-16 h-16 mr-6 rounded overflow-hidden">
                        <!-- TODO: HANDLE image === null -->
                        @if(item.listingVariation.image){
                          <img 
                            [src]="item.listingVariation.image.url"
                            class=""
                          />
                        }
                      </div>
                      <div>
                        <div class=" font-bold">{{item.listingVariation.listing.name}}</div>
                        <div class=" text-sm">{{item.listingVariation.name}}</div>
                        <div class=" text-sm">{{item.listingVariation.description}}</div>
                        <div class=" text-sm font-bold">{{item.pricePerItemAtTimeOfOrder * item.quantity| currency}}</div>
                        @if(item.isDigitalDownload){
                          <msh-button class="mt-3" (onClick)="onViewDownloadFiles.emit(fulfillment.fulfillmentNumber)" [cta]="'View Download Files'"></msh-button>
                        }
                      </div>
                    </div>
                  }
                }
              </div>
              <div class=" flex justify-between flex-col min-h-32">
                <div class=" font-bold text-right">Fulfillment #{{fulfillment.fulfillmentNumber}}</div>
                @if(fulfillment.trackingNumber){
                  <div class=" text-sm font-bold text-orange-400">Tracking #{{fulfillment.trackingNumber}}</div>
                }
                <div class="flex justify-end relative">
                  @if(
                    fulfillment.statusId 
                    && ctaMap[fulfillment.statusId]
                    && order.orderItems_by_fulfillment
                    && (!fulfillment.refunds_on_fulfillment || fulfillment.refunds_on_fulfillment.length !== order.orderItems_by_fulfillment[fulfillment.id].length)
                  ){
                    <msh-button class="relative" 
                      (onClick)="actionButtonClicked(fulfillment)" 
                      [isLoading]="takingAction"
                      [buttonDisplayMap]="ctaMap[fulfillment.statusId]"
                      [optionData]="{fulfillment, order}"
                    ></msh-button>
                  }
                </div>
              </div>
            </div>
            <msh-refund-details [fulfillment]="fulfillment" [fulfillmentStatusMap]="fulfillmentStatusMap" (printReturnLabel)="printReturnLabel($event)"></msh-refund-details>
            @if(fulfillment.reviews_on_fulfillment && fulfillment.reviews_on_fulfillment.length > 0 ){
              <div class=" flex items-center justify-around mt-4">
                @if(buyerReviews[fulfillment.id]; as review){
                  <msh-review-tile 
                    class="w-5/12"
                    (click)="onOpenReviewDialog.emit(review)"
                    [review]="review"
                  ></msh-review-tile>
                }@else { <div class="null">Review about the seller needed</div> }
                @if(sellerReviews[fulfillment.id]; as review){
                  <msh-review-tile 
                    class="w-5/12"
                    (click)="onOpenReviewDialog.emit(review)"
                    [review]="review"
                  ></msh-review-tile>
                }@else { <div class="null">No review from seller yet</div> }
              </div>
            }
            <div>
              <div (click)="toggleInteraction(fulfillment.id)" class=" min-h-8 cursor-pointer font-bold flex items-center justify-end text-orange-400 text-right">
                <div>{{ fulfillmentStatusMap[fulfillment.statusId] ? fulfillmentStatusMap[fulfillment.statusId].description : fulfillment.statusId}}</div>
                <div class=" py-2 min-w-8 flex items-center">
                  @if(gettingInteractionsMap[fulfillment.id]){
                    <div class="msh-loading-sm orange">&nbsp;</div>
                  }@else{
                    <mat-icon>{{interactions()[fulfillment.id] && interactions()[fulfillment.id].show ? 'expand_less' : 'expand_more'}}</mat-icon>
                  }
                </div>
              </div>
            </div>
            @if(interactions() && interactions()[fulfillment.id] && interactions()[fulfillment.id].show && interactions()[fulfillment.id]; as interaction){
              <div class="ml-8 md:ml-32">
                @if(!showMessageInput){
                  <div class="lil-action ml-3" (click)="showMessageInput = true">Write a message to the seller</div>
                }@else {
                  <div class="m-2 ml-3">
                    <textarea
                      [(ngModel)]="message"
                      [placeholder]="'Message to ' +locations[fulfillment.fulfilledByLocationId].name"
                      rows="7"
                      class="border"
                    ></textarea>
                    <div class="flex justify-between">
                      <button mat-flat-button (click)="showMessageInput = false">Cancel</button>
                      <msh-button
                        [cta]="'Send Message'"
                        (onClick)="sendMsg(fulfillment)"
                        [isLoading]="messageSending"
                        [disabled]="!message"
                        class=" min-w-60"
                      ></msh-button>
                    </div>
                  </div>
                }
              </div>
              <div class="mt-6">
                @for(entry of interaction.entries; track entry.id){
                  <msh-interaction-entry [isBuyer]="true" [interaction]="entry"></msh-interaction-entry>
                }
              </div>
            }
          </div>
        }
      }
    }
  }
  `,
  styles: ``
})
export class PurchaseOrderDetailsComponent extends Base<PurchaseOrderDetailsComponent> implements OnChanges {
  @Input() order!: OrderPublic
  @Input() ctaMap!:  {[status in FulfillmentStatusType]?: ButtonDisplay }
  @Input() takingAction = false;
  @Input() fulfillmentStatusMap: {[statusId: string]: Status} = {}
  @Input() locations:  {[locationId: string]: LocationPublic} = {}; 
  @Input() buyerReviews: {[fulfillmentId: string]: ReviewForEdit} = {};
  @Input() sellerReviews: {[fulfillmentId: string]: ReviewForEdit} = {}
  @Output() onActionTaken: EventEmitter<Fulfillment> = new EventEmitter();
  @Output() onOpenReviewDialog: EventEmitter<ReviewForEdit> = new EventEmitter();
  @Output() onViewDownloadFiles: EventEmitter<number> = new EventEmitter();
  location?: LocationPublic | null
  interactions: Signal<{[fulfillmentId: string]: InteractionView}> = toSignal( inject(Store).select(interactionsFeature.selectInteractionsByFulfillmentId)) as Signal<{[fulfillmentId: string]: InteractionView}>;
  message = "";
  messageSending = false;
  showMessageInput = false;
  dateTimeFormat = dateTimeFormat
  gettingInteractionsMap: {[fulfillmentId: string]: boolean} = {}

  constructor(
    private store: Store,
    private snackBar: MatSnackBar,
    private orderService: OrderService,
    private router: Router, 
  ) {
    super();
    this.store.select(accountFeature.selectLocations).pipe(takeUntil(this.$destroy)).subscribe(async locations => {
      this.locations = locations as {[locationId:string]: LocationPublic}
    })
    // scroll to top after action is taken
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if(changes['order']){
      console.log({order: this.order});

      const nonArchivedOrCompletedFulfillments = this.order.fulfillments_on_order.filter(f => f.statusId !== 'archived' && ( !f.trackingNumber || f.trackingNumber === ''))
      if(nonArchivedOrCompletedFulfillments.length === 1){
        this.toggleInteraction(nonArchivedOrCompletedFulfillments[0].id!, true);
      }
    }
  }

  async toggleInteraction(fulfillmentId: string, open: boolean | null = null){
    this.gettingInteractionsMap[fulfillmentId] = true;
    const state = await lastValueFrom(this.store.select(interactionsFeature.selectInteractionsByFulfillmentId).pipe(take(1)));
    if(!state[fulfillmentId] || !state[fulfillmentId].show){
      const location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(take(1)));
      if(!location) {
        snackBarError(`Lost connection with your ${LOCATION_LABEL}. Please refresh.`, this.snackBar);
        return;
      }
      this.location = location;
      await this.orderService.hydrateBuyerInteractions(fulfillmentId, location.id);
    }
    this.store.dispatch(InteractionsPageActions.toggleInteractionsShowing({fulfillmentId, open}))
    this.gettingInteractionsMap[fulfillmentId] = false;
  }

  actionButtonClicked(fulfillment: Fulfillment){
    this.onActionTaken.emit(fulfillment)
  }

  async sendMsg(fulfillment: Fulfillment){
    this.messageSending = true;
    const vars: SendMessageVariables = {
      buyerInitiated: true,
      fulfillmentId: fulfillment.id,
      buyerId: fulfillment.fulfilledToLocationId,
      sellerId: fulfillment.fulfilledByLocationId,
      buyerAlert: false,
      sellerAlert: true,
      isInitial: false,
      subject: "Message Sent",
      body: this.message,
    }
    const sendMessageRef = await sendMessage(vars);
    if(!this.location) this.location =  await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(take(1)));
    if(this.location && this.order.id) await this.orderService.hydrateBuyerInteractions(fulfillment.id, this.location.id);
    this.message = ""
    this.showMessageInput = false;
    this.messageSending = false;
  }


  printReturnLabel(refund: RefundPublic){
    if(refund.labelUrl){
      window.open(refund.labelUrl, '_blank');
    }else{
      this.snackBar.open("No label URL found for refund", "Error", { duration: 3000 });
    }
  }

  viewDownloadFiles(fulfillmentNumber: number){
    this.onViewDownloadFiles.emit(fulfillmentNumber);
  }
}
